import { TrustOrganization } from "./trust";

/**
 * Represents a user.
 */
export type User = {
  /**
   * The user's unique identifier
   * @type {number}
   * @memberof User
   * @property id
   * @required
   * @example
   * 2
   */
  id: number;

  /**
   * The user's unique identifier
   * @type {string}
   * @memberof User
   * @property uuid
   * @required
   * @example
   * "f380e9cf-2f18-4508-8fc2-38f945a1d517"
   */
  uuid: string;

  /**
   * The user's name to display
   * @type {string}
   * @memberof User
   * @property name
   * @required
   * @example
   * "johnsmith23"
   */
  name: string;

  /**
   * The user's phone to display
   * @type {string}
   * @memberof User
   * @property phone
   * @required
   * @example
   * "+9477881924"
   */
  phone: string;

  /**
   * The user's country to display
   * @type {string}
   * @memberof User
   * @property country
   * @required
   * @example
   * "Sri Lanka"
   */
  country: string;

  /**
   * The user's country code to display
   * @type {number}
   * @memberof User
   * @property countryCode
   * @required
   * @example
   * 94
   */
  countryCode: number;

  /**
   * The user's name to display
   * @type {string}
   * @memberof User
   * @property username
   * @required
   * @example
   * "johnsmith23"
   */
  username: string;

  /**
   * The user's email address
   * @type {string}
   * @memberof User
   * @property email
   * @required
   * @example
   * "john.smith@welcomedeveloper.com"
   */
  email: string;

  /**
   * Show is email address verified
   * @type {boolean}
   * @memberof User
   * @property emailVerified
   * @required
   * @example
   * true | false
   */
  emailVerified: boolean;

  /**
   * The user's created date
   * @type {Date}
   * @memberof User
   * @property createdAt
   * @required
   * @example
   * "2020-01-01T00:00:00.000Z"
   */
  createdAt: Date;

  /**
   * The user's updated date
   * @type {Date}
   * @memberof User
   * @property updatedAt
   * @required
   * @example
   * "2020-01-01T00:00:00.000Z"
   */
  updatedAt: Date;

  /**
   * The user's deleted date
   * @type {Date}
   * @memberof User
   * @property deletedAt
   * @optional
   * @example
   * "2020-01-01T00:00:00.000Z"
   */
  deletedAt?: Date;

  /**
   * The user's status
   * @type {UserStatus}
   * @memberof User
   * @property status
   * @required
   * @example
   * "active" | "blocked"
   */
  status: UserStatus;

  /**
   * The user's role
   * @type {UserRoles}
   * @memberof User
   * @property role
   * @required
   * @example
   * "user" | "manager" | "admin" | "trust"
   */
  role: UserRoles;

  /**
   * The user's role
   * @type {TrusteeStatus}
   * @memberof User
   * @property trusteeStatus
   * @required
   * @example
   */
  trusteeStatus: TrusteeStatus;

  /**
   * The user's role
   * @type {TrustStatus}
   * @memberof User
   * @property trustStatus
   * @required
   * @example
   */
  trustStatus: TrustStatus;

  /**
   * The user's verification status
   * @type {number}
   * @memberof User
   * @property kycLvl
   * @required
   * @example
   * 0 | 1 | 2
   */
  kycLvl: number;

  /**
   * The user's wallet address
   * @type {string}
   * @memberof User
   * @property address
   * @required
   * @example
   * "0x30e8d91c57cfe4a2bffffa9f91f864052b61c1ab"
   */
  address: string;

  /**
   * The user's description
   * @type {string}
   * @memberof User
   * @property bio
   * @example
   * "I'm good"
   * @default
   * null
   */
  bio: string;

  /**
   * The user's first name
   * @type {string}
   * @memberof User
   * @property firstName
   * @required
   * @example
   * "John"
   */
  firstName: string;

  /**
   * The user's last name
   * @type {string}
   * @memberof User
   * @property lastName
   * @required
   * @example
   * "Doe"
   */
  lastName: string;

  /**
   * The user's patronymic
   * @type {string}
   * @memberof User
   * @property patronymic
   * @required
   * @example
   * "Jovanovich"
   */
  patronymic: string;

  /**
   * Link to user's profile image
   * @type {string}
   * @memberof User
   * @property profileImageUrl
   * @required
   * @example
   * "http://localhost:3100/profile.png"
   */
  profileImageUrl: string;

  /**
   * Link to user's profile image
   * @type {string}
   * @memberof User
   * @property profilePicture
   * @required
   * @example
   * "http://localhost:3100/profile.png"
   */
  profilePicture: string;
  /**
   * Link to user's proof of id
   * @type {string}
   * @memberof User
   * @property proofOfId
   * @required
   * @example
   * "http://localhost:3100/profile.png"
   */
  proofOfId: string;

  /**
   * User's own referrerCode
   * @type {string}
   * @memberof User
   * @property referrerCode
   * @required
   * @example
   * "http://localhost:3100/profile.png"
   */
  referrerCode: string;

  /**
   * If user had been added to an organization, this will indicate the user to upload kyc
   * @type {boolean}
   * @memberof User
   * @property proofOfIdRequired
   * @required
   * @example
   * "http://localhost:3100/profile.png"
   */
  proofOfIdRequired: boolean;

  /**
   * User's referrer address
   * @type {string}
   * @memberof User
   * @property referrerAddress
   * @required
   * @example
   * "http://localhost:3100/profile.png"
   */
  referrerAddress: string;

  treeBalance: string;
  lastReferredAt: string;

  /**
   * IPFS legal documents this wallet has already signed.
   * Present on the user payload and on each organization secondary wallet.
   */
  signedDocuments?: UserDocumentRef[];

  /**
   * IPFS legal documents awaiting this wallet's signature.
   */
  pendingDocuments?: UserDocumentRef[];

  organization?: TrustOrganization;
  pendingReassignment?: {
    hasPendingReassignment: boolean;
    reassignments: [
      {
        tokenId: number;
        etNftId: number;
      }
    ];
  };

  kycStatus: KycStatus;
  trustAdminStatus: TrustAdminStatus;
};


/**
 * A reference to an IPFS legal document on a user's signedDocuments /
 * pendingDocuments arrays.
 */
export type UserDocumentRef = {
  id: number;
  type: string;
  /** Only on signedDocuments — true when this wallet created the document. */
  creator?: boolean;
};

export enum TrustAdminStatus {
  INVITED = 'invited',
  ACCEPTED = 'accepted',
  VERIFIED = 'verified',
  DECLINED = 'declined',
}
export enum KycStatus {
  KYC_REQUIRED = 'KYC_REQUIRED',
  KYC_NOT_REQUIRED = 'KYC_NOT_REQUIRED',
  KYC_PENDING = 'KYC_PENDING',
  KYC_VERIFIED = 'KYC_VERIFIED',
  KYC_DECLINED = 'KYC_DECLINED',
}

export enum UserRoles {
  USER = "user", // patron; pioneer once referrerAddress is set (see useAuthorization)
  MANAGER = "manager", // not used
  TRUST = "trust", // trustee
  ADMIN = "admin", // trust admin
  GUARDIAN = "guardian", // set by the backend on GuardianAdded, i.e. once a land is minted
}

export enum UserStatus {
  ACTIVE = "active",
  BLOCKED = "blocked",
}

export enum TrusteeStatus {
  INVITED = "invited", // user was invited to become trustee
  ACCEPTED = "accepted", // user has uploaded all required documents and accepted trustee status
  VERIFIED = "verified", // user was verified by admin
  DECLINED = "declined", // user declined trustee status and removes assigned organization
}

export enum TrustStatus {
  INVITED = "invited", // user was invited to become trustee
  ACCEPTED = "accepted", // user has uploaded all required documents and accepted trustee status
  VERIFIED = "verified", // user was verified by admin
  DECLINED = "declined", // user declined trustee status and removes assigned organization
}
